import axios from 'axios'

/** Shared axios instance — one set of interceptors for the whole app. */
let singleton = null

/**
 * HttpClient factory (singleton).
 * Call as `HttpClient()` — same API as before, same instance every time.
 */
const createHttpClient = () => {
  if (singleton) return singleton

  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '',
    timeout: 10000, // 10 segundos
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Interceptor para requests
  instance.interceptors.request.use(
    (config) => {
      const url = String(config.url || '')
      const isAuthAttempt = /\/user\/(login|register)(?:\?|$)/.test(url)

      // No enviar token caducado en login/registro: Auth middleware devuelve 401 antes del handler
      if (isAuthAttempt) {
        delete config.headers.Authorization
        return config
      }

      // Actualizar token en cada request por si cambió
      const currentToken = localStorage.getItem('token')
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`
      }
      // FormData: dejar que el navegador fije multipart + boundary
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        if (config.headers && typeof config.headers.delete === 'function') {
          config.headers.delete('Content-Type')
        } else if (config.headers) {
          delete config.headers['Content-Type']
          delete config.headers['content-type']
        }
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Interceptor para responses
  instance.interceptors.response.use(
    (response) => {
      // Si la respuesta tiene el formato estándar, extraer los datos
      if (response.data && response.data.success !== undefined) {
        return {
          ...response,
          data: response.data.data || response.data,
          meta: response.data.meta,
          viewType: response.data.viewType,
          success: response.data.success,
          message: response.data.message,
        }
      }
      return response
    },
    (error) => {
      // Cancelación: preservar semántica de cancel para que el caller pueda detectarlo
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || (typeof axios?.isCancel === 'function' && axios.isCancel(error))) {
        return Promise.reject(error)
      }

      // Manejo centralizado de errores (propagar message, error y code del backend)
      const errorResponse = {
        message: 'An unexpected error occurred',
        status: error.response?.status,
        data: null,
      }

      if (error.response) {
        // Error del servidor con respuesta
        const { data, status } = error.response

        errorResponse.status = status
        const backendMsg = data?.message || data?.error || null
        errorResponse.message = backendMsg || `Server error: ${status}`
        errorResponse.error = data?.error
        errorResponse.code = data?.code
        errorResponse.details = data?.details

        // Manejo específico por código de estado (solo sobreescribe si backend no envió mensaje)
        switch (status) {
          case 401: {
            const reqUrl = String(error.config?.url || '')
            const isLoginAttempt = reqUrl.includes('/user/login')
            // No redirigir en fallo de login: el formulario debe mostrar el mensaje
            if (!isLoginAttempt) {
              localStorage.removeItem('token')
              localStorage.removeItem('user')
              window.location.href = '/entrar'
              if (!backendMsg) errorResponse.message = 'Session expired. Please login again.'
            }
            break
          }
          case 403:
            if (!backendMsg) errorResponse.message = 'You do not have permission to perform this action.'
            break
          case 404:
            if (!backendMsg) errorResponse.message = 'Resource not found.'
            break
          case 409:
            if (!backendMsg) errorResponse.message = 'Conflict with existing data.'
            break
          case 422:
            if (!backendMsg) errorResponse.message = 'Validation failed.'
            break
          case 500:
            if (!backendMsg) errorResponse.message = 'Internal server error. Please try again later.'
            break
          case 400:
            if (!backendMsg) errorResponse.message = 'Solicitud inválida (400).'
            break
          default:
            if (!backendMsg) {
              errorResponse.message =
                status != null && status !== 0
                  ? `Error del servidor (${status})`
                  : 'Error de red o servidor no disponible.'
            }
        }
      } else if (error.request) {
        // Error de red / proxy caído (status 0)
        errorResponse.message =
          'No hay respuesta del servidor (¿API en :5000 y proxy activos?).'
        errorResponse.status = 0
      } else {
        // Error de configuración o throw previo (p. ej. transform)
        errorResponse.message = error.message || 'Request configuration error'
        if (error.status != null) errorResponse.status = error.status
      }

      // Evitar que RA muestre solo "Error: 0"
      if (!errorResponse.message || errorResponse.message === `Error: ${errorResponse.status}`) {
        errorResponse.message =
          errorResponse.status === 0
            ? 'No hay respuesta del servidor (¿API en :5000 y proxy activos?).'
            : `Error del servidor (${errorResponse.status || 'desconocido'})`
      }

      return Promise.reject(errorResponse)
    }
  )

  singleton = instance
  return singleton
}

export default createHttpClient
